import * as React from 'react';
import {Suspense, useDeferredValue, useState} from 'react';
import {fetchQuery, graphql, useLazyLoadQuery, useMutation, useRelayEnvironment} from 'react-relay';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {AlertTriangle, X} from 'lucide-react';
import ScanInput from './ScanInput';
import {NewCustomerForm} from './OpsCustomersPage';
import {OpsReservationNewPageCustomersQuery} from './__generated__/OpsReservationNewPageCustomersQuery.graphql';
import {OpsReservationNewPageAssetQuery} from './__generated__/OpsReservationNewPageAssetQuery.graphql';
import {OpsReservationNewPageAvailabilityQuery} from './__generated__/OpsReservationNewPageAvailabilityQuery.graphql';
import {OpsReservationNewPageCreateMutation} from './__generated__/OpsReservationNewPageCreateMutation.graphql';
import {defaultReservationWindow, formatDateTime, fromLocalInput, ghostButton, inputClass, primaryButton} from './lib';

const CUSTOMERS = graphql`
    query OpsReservationNewPageCustomersQuery($search: String) {
        customers(search: $search, first: 20) {
            nodes { id name email phone }
        }
    }`;

const ASSET = graphql`
    query OpsReservationNewPageAssetQuery($assetTag: AssetTag!) {
        asset(assetTag: $assetTag) {
            assetTag
            name
            category
            status
            dailyRate
            kitMembers { id }
        }
    }`;

const AVAILABILITY = graphql`
    query OpsReservationNewPageAvailabilityQuery($assetTags: [AssetTag!]!, $startsAt: ISO8601DateTime!, $endsAt: ISO8601DateTime!) {
        availability(assetTags: $assetTags, startsAt: $startsAt, endsAt: $endsAt) {
            message
            asset { assetTag }
        }
    }`;

const CREATE = graphql`
    mutation OpsReservationNewPageCreateMutation($input: CreateReservationInput!) {
        createReservation(input: $input) {
            reservation { id }
            userErrors { message assetTag }
        }
    }`;

type Customer = { id: string; name: string; email?: string | null; phone?: string | null };
type Item = { assetTag: string; name: string; category: string; pieces: number; dailyRate: string | null };

// Shared with the ops engagement builder's customer step.
export const CustomerResults: React.FC<{ search: string; onPick: (c: Customer) => void }> = ({search, onPick}) => {
    const data = useLazyLoadQuery<OpsReservationNewPageCustomersQuery>(CUSTOMERS, {search: search || null});
    const rows = (data.customers.nodes ?? []).flatMap((c) => (c ? [c] : []));
    if (rows.length === 0) return <p className="text-gray-500 text-xs px-1">No matches — add them below.</p>;
    return (
        <ul className="border border-gray-800 divide-y divide-gray-900 max-h-56 overflow-y-auto">
            {rows.map((c) => (
                <li key={c.id}>
                    <button type="button" onClick={() => onPick(c)}
                            className="w-full text-left px-3 py-2 hover:bg-white/[0.04] flex gap-4">
                        <span className="text-white text-sm">{c.name}</span>
                        <span className="text-gray-500 text-xs">{c.email ?? c.phone ?? ''}</span>
                    </button>
                </li>
            ))}
        </ul>
    );
};

const Availability: React.FC<{ tags: string[]; startsAt: string; endsAt: string }> = ({tags, startsAt, endsAt}) => {
    const data = useLazyLoadQuery<OpsReservationNewPageAvailabilityQuery>(AVAILABILITY, {
        assetTags: tags, startsAt: fromLocalInput(startsAt), endsAt: fromLocalInput(endsAt),
    }, {fetchPolicy: 'network-only'});
    if (data.availability.length === 0) {
        return <p className="text-emerald-400 text-xs">✓ Everything is available for these dates.</p>;
    }
    return (
        <ul className="text-xs text-red-400 space-y-1">
            {data.availability.map((c, i) => (
                <li key={i} className="flex items-start gap-2"><AlertTriangle className="w-3 h-3 mt-0.5 shrink-0"/>{c.message}</li>
            ))}
        </ul>
    );
};

const OpsReservationNewPage: React.FC = () => {
    const environment = useRelayEnvironment();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const [commit, inFlight] = useMutation<OpsReservationNewPageCreateMutation>(CREATE);

    const [customer, setCustomer] = useState<Customer | null>(
        params.get('customer') ? {id: params.get('customer')!, name: 'Selected customer'} : null,
    );
    const [customerSearch, setCustomerSearch] = useState('');
    const deferredCustomerSearch = useDeferredValue(customerSearch);
    const [showNewCustomer, setShowNewCustomer] = useState(false);
    const [window, setWindow] = useState(defaultReservationWindow());
    const [notes, setNotes] = useState('');
    const [items, setItems] = useState<Item[]>([]);
    const [errors, setErrors] = useState<string[]>([]);

    const datesValid = Boolean(window.startsAt && window.endsAt) && new Date(window.endsAt) > new Date(window.startsAt);

    const addItem = async (tag: string): Promise<string | null> => {
        if (items.some((i) => i.assetTag === tag)) return `${tag} is already on the list`;
        const result = await fetchQuery<OpsReservationNewPageAssetQuery>(environment, ASSET, {assetTag: tag}).toPromise();
        const asset = result?.asset;
        if (!asset) return `No asset with tag ${tag}`;
        if (['LOST', 'RETIRED', 'MAINTENANCE'].includes(asset.status)) return `${tag} is ${asset.status.toLowerCase()}`;
        setItems((prev) => [...prev, {
            assetTag: asset.assetTag, name: asset.name, category: asset.category,
            pieces: asset.kitMembers.length, dailyRate: asset.dailyRate,
        }]);
        return null;
    };

    const submit = () => {
        if (!customer || !datesValid) return;
        commit({
            variables: {
                input: {
                    customerId: customer.id,
                    startsAt: fromLocalInput(window.startsAt),
                    endsAt: fromLocalInput(window.endsAt),
                    notes: notes || null,
                    assetTags: items.map((i) => i.assetTag),
                },
            },
            onCompleted: (res) => {
                const payload = res.createReservation;
                if (payload?.reservation) {
                    navigate(`/ops/reservations/${encodeURIComponent(payload.reservation.id)}`);
                } else {
                    setErrors(payload?.userErrors.map((u) => u.message) ?? ['Something went wrong']);
                }
            },
            onError: (err) => setErrors([err.message]),
        });
    };

    return (
        <div className="max-w-4xl">
            <h1 className="text-3xl font-display font-bold mb-6">New reservation</h1>

            <section className="mb-8">
                <h2 className="text-xs uppercase tracking-widest text-gray-500 mb-2">Customer</h2>
                {customer ? (
                    <div className="flex items-center gap-3">
                        <span className="text-white font-semibold">{customer.name}</span>
                        {customer.email && <span className="text-gray-500 text-sm">{customer.email}</span>}
                        <button type="button" onClick={() => setCustomer(null)} className="text-xs text-gray-400 hover:text-white">change</button>
                    </div>
                ) : (
                    <div className="space-y-3">
                        <input autoFocus value={customerSearch} onChange={(e) => setCustomerSearch(e.target.value)}
                               placeholder="Search customers…" className={`${inputClass} w-full md:w-96`}/>
                        <Suspense fallback={<p className="text-gray-500 text-xs">Searching…</p>}>
                            <CustomerResults search={deferredCustomerSearch} onPick={setCustomer}/>
                        </Suspense>
                        {showNewCustomer ? (
                            <NewCustomerForm onCreated={(c) => { setCustomer(c); setShowNewCustomer(false); }}/>
                        ) : (
                            <button type="button" onClick={() => setShowNewCustomer(true)} className={ghostButton}>New customer</button>
                        )}
                    </div>
                )}
            </section>

            <section className="mb-8">
                <h2 className="text-xs uppercase tracking-widest text-gray-500 mb-2">Dates</h2>
                <div className="flex flex-col md:flex-row gap-3 md:items-center">
                    <input type="datetime-local" value={window.startsAt} className={inputClass}
                           onChange={(e) => setWindow({...window, startsAt: e.target.value})}/>
                    <span className="text-gray-600">→</span>
                    <input type="datetime-local" value={window.endsAt} className={inputClass}
                           onChange={(e) => setWindow({...window, endsAt: e.target.value})}/>
                    {!datesValid && <span className="text-red-400 text-xs">End must be after start.</span>}
                </div>
            </section>

            <section className="mb-8">
                <h2 className="text-xs uppercase tracking-widest text-gray-500 mb-2">Gear</h2>
                <ScanInput placeholder="Scan a tag or type it and press Enter (kits or items)…" onScan={addItem} autoFocus={false}/>
                {items.length > 0 && (
                    <ul className="mt-3 border border-gray-800 divide-y divide-gray-900">
                        {items.map((item) => (
                            <li key={item.assetTag} className="flex items-center gap-4 px-3 py-2">
                                <span className="font-mono text-xs text-gray-400 w-36">{item.assetTag}</span>
                                <span className="text-white text-sm flex-1">
                                    {item.name}
                                    {item.pieces > 0 && <span className="text-gray-500 text-xs"> · {item.pieces} pieces</span>}
                                </span>
                                <span className="text-xs text-gray-500">{item.category.replace(/_/g, ' ')}</span>
                                {item.dailyRate && <span className="text-lumina-accent text-xs">${item.dailyRate}/day</span>}
                                <button type="button" onClick={() => setItems(items.filter((i) => i !== item))}
                                        className="text-gray-500 hover:text-red-400" title="Remove"><X className="w-4 h-4"/></button>
                            </li>
                        ))}
                    </ul>
                )}
                {items.length > 0 && datesValid && (
                    <div className="mt-3">
                        <Suspense fallback={<p className="text-gray-500 text-xs">Checking availability…</p>}>
                            <Availability tags={items.map((i) => i.assetTag)} startsAt={window.startsAt} endsAt={window.endsAt}/>
                        </Suspense>
                    </div>
                )}
            </section>

            <section className="mb-8">
                <h2 className="text-xs uppercase tracking-widest text-gray-500 mb-2">Notes</h2>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
                          className={`${inputClass} w-full`} placeholder="Shoot location, delivery, anything the pick crew should know…"/>
            </section>

            {errors.length > 0 && (
                <ul className="mb-4 text-sm text-red-400 space-y-1">{errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
            )}

            <div className="flex items-center gap-4">
                <button type="button" onClick={submit} disabled={!customer || !datesValid || inFlight} className={primaryButton}>
                    Create reservation
                </button>
                {customer && datesValid && (
                    <span className="text-xs text-gray-500">
                        {formatDateTime(fromLocalInput(window.startsAt))} → {formatDateTime(fromLocalInput(window.endsAt))}
                        {items.length > 0 && ` · ${items.length} line${items.length === 1 ? '' : 's'}`}
                    </span>
                )}
            </div>
        </div>
    );
};

export default OpsReservationNewPage;
